import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerChatPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { fetchChat(); const i = setInterval(fetchChat, 10000); return () => clearInterval(i); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fetchChat = async () => {
    try {
      const res = await fetch(`${API}/api/chat/public/${token}`);
      if (!res.ok) throw new Error("Chat not found");
      const d = await res.json();
      setData(d);
      setMessages(d.messages || []);
      if (!customerName && d.channel?.name) setCustomerName(d.channel.name);
    } catch (err) {
      setError("Chat not found or link expired");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      await fetch(`${API}/api/chat/public/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: newMsg.trim(), name: customerName })
      });
      setNewMsg("");
      fetchChat();
    } catch (err) {
      // Silent
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  if (error) return <div className="flex items-center justify-center h-screen text-slate-500">{error}</div>;

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <div className="bg-indigo-600 text-white px-4 py-3 shrink-0">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <div>
            <h1 className="font-semibold">{data?.org_name || "Chat"}</h1>
            <p className="text-xs text-indigo-200">Chat with our team</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 mt-12">
            <MessageCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p>Send a message to start chatting with {data?.org_name || "the team"}</p>
          </div>
        )}
        {messages.map(msg => {
          const isMine = msg.is_customer;
          return (
            <div key={msg.message_id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${isMine ? "bg-indigo-600 text-white rounded-br-sm" : "bg-white border text-slate-800 rounded-bl-sm"}`}>
                {!isMine && <p className="text-[10px] font-semibold text-indigo-600 mb-0.5">{msg.author?.name || "Team"}</p>}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                <p className={`text-[10px] mt-0.5 ${isMine ? "text-indigo-200" : "text-slate-400"}`}>{new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="border-t bg-white px-4 py-3 shrink-0">
        <div className="flex gap-2">
          <Input value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Type your message..." className="flex-1"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSend(); } }} />
          <Button onClick={handleSend} disabled={sending || !newMsg.trim()} className="bg-indigo-600 hover:bg-indigo-700">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
